import { useLazyQuery } from '@apollo/client';
import {
	ConfiguredCarIdentifier,
	FinancingInput,
	FinancingProductParameterInput,
	InputMaybe,
} from '@oneaudi/onegraph-client';
import { prStringToConfiguredCarInput } from '@oneaudi/onegraph-utils';
import { useAtom, useAtomValue } from 'jotai';
import { useContext, useEffect, useState } from 'react';
import { Footnote } from '@oneaudi/footnote-service/dist/cjs/types/footnotes-response.type';
import { AppStateContext, useAppServices } from '~/app/contexts/app-context';
import { useDialogs } from '~/app/hooks/use-dialogs';
import { MarketUiProvider } from '~/app/market-config/market-ui-context';
import MarketRouter from '~/app/market-router';
import ErrorPage from '~/app/pages/error';
import { injectParent } from '~/app/parent';
import { AppStyle } from '~/app/styles/app-style';
import { NgcCarIdentifier } from '~/app/utils/graphql-sub-types';
import isConsFinancingStage from '~/app/utils/is-cons-financing-stage';
import mapFinanceDataToRequest from '~/app/utils/map-finance-data';
import { normalizeCarIdentifier } from '~/app/utils/normalizeCarIdentifier';
import { RelevantRescueCodes } from '~/components/dialog/rescue-info-dialog';
import { CarByCodeDocument, DefaultCarIdentifierDocument } from '~/graphql/__generated__/graphql';
import { ConfiguratorProvider } from '~/graphql/hooks/configurator-context';
import { state } from '~/storage';
import { documentAvailable, windowAvailable } from '~/utils/client-detection';
import { expect } from '~/utils/unwrap';
import '../tailwind.css';
import { AemMarketConfig, MarketConfigProvider } from './market-config';
import { CAE_REMARK } from '~/components/footnotes/constants';

export function AppContent() {
	const SCRIPT_SRC = isConsFinancingStage()
		? 'https://cdn.cons.fts.webcalc.vwfs.io/2-latest/bronson-bundled/calculator.js'
		: 'https://cdn.fts.webcalc.vwfs.io/2-latest/bronson-bundled/calculator.js';
	const isBodyNoneScroll = useAtomValue(state.isBodyNoneScroll);
	const [currentView, setCurrentView] = useAtom(state.currentView);
	const [, setEntryType] = useAtom(state.entryType);
	const [, setRescueInfoAdjustments] = useAtom(state.rescueInfoAdjustments);
	const [, setPresetFinanceProduct] = useAtom(state.fsagFinancialCalculatorSettings);
	const appState = useContext(AppStateContext);
	const aemConfig = appState?.content || ({} as AemMarketConfig);
	const { openDialog } = useDialogs();
	const {
		featureServices: { footnoteService },
	} = useAppServices();

	// Unfortunately, for now the FSAG financial service with UI is only available as CDN JS lib
	useEffect(() => {
		const script = document.createElement('script');

		script.src = SCRIPT_SRC;
		script.async = true;

		document.body.appendChild(script);
		return () => {
			document.body.removeChild(script);
		};
	}, [SCRIPT_SRC]);

	// initialize AVP 3d showroom
	useEffect(() => {
		const script = document.createElement('script');

		script.src = 'https://avp-3dws-deck.eu-west-1-web-streaming.avp.tech/avp-3dws-deck.js';
		script.type = 'module';

		document.body.appendChild(script);

		return () => {
			document.body.removeChild(script);
		};
	}, []);

	// Initialize empty CAE footnote to always be able to just replace it with new data
	useEffect(() => {
		const footnote = [{ Key: CAE_REMARK, Text: '' } as Footnote];
		footnoteService?.addFootNotes(footnote);
	}, []);

	// Also, the CSS for the FSAG financial Service is also distributed via CDN only
	useEffect(() => {
		const link = document.createElement('link');
		link.type = 'text/css';
		link.rel = 'stylesheet';
		link.href = 'https://cdn.bronson.vwfs.io/audi/v/14.2.0/css/style.scoped.min.css';

		document.body.appendChild(link);
		return () => {
			document.body.removeChild(link);
		};
	}, []);

	useEffect(() => {
		const handleHashChange = () => {
			const hash = windowAvailable() ? window?.location?.hash : undefined;
			const urlParams = new URLSearchParams(window.location.search);
			if (hash === '#summary') {
				setCurrentView('summary');
			} else if (hash === '#configurator' || hash === '') {
				setCurrentView('configurator');
				const newUrl = `${window.location.pathname}?${urlParams.toString()}#configurator`;
				window.history.replaceState({ path: newUrl }, '', newUrl);
			}
		};
		window.addEventListener('hashchange', handleHashChange);
		handleHashChange();
		return () => {
			window.removeEventListener('hashchange', handleHashChange);
		};
	}, [setCurrentView]);

	useEffect(() => {
		const scrollTop = window.scrollY || document.documentElement.scrollTop;
		const scrollLeft = window.scrollX || document.documentElement.scrollLeft;

		const disableScroll = () => {
			if (isBodyNoneScroll) {
				window.scrollTo(scrollLeft, scrollTop);
			}
		};

		window.addEventListener('scroll', disableScroll);
		return () => window.removeEventListener('scroll', disableScroll);
	}, [isBodyNoneScroll]);

	useEffect(() => {
		injectParent();

		const elem = document.querySelector('body .nm-wrapper');
		const elemContent = document.querySelector('body .nm-wrapper-content');
		if (!elem) return;

		(elem as HTMLDivElement).style.maxWidth = '100%';
		if (!elemContent) return;
		(elemContent as HTMLDivElement).style.margin = '0';
	}, []);
	const html = documentAvailable() ? document.getElementsByTagName('html') : null;
	if (html && html.length) {
		html[0].style.fontSize = '100%';
	}

	const [getCarByCode] = useLazyQuery(CarByCodeDocument);
	const [getDefaultCarIdentifier] = useLazyQuery(DefaultCarIdentifierDocument);

	const [carIdentifier, setCarIdentifier] = useState<NgcCarIdentifier | null>(null);
	const [initFinancingInput, setInitFinancingInput] = useState<FinancingInput | undefined>(undefined);

	useEffect(() => {
		// Configuration entry (either by audicode, pr string, or default)
		const audiCode = new URLSearchParams(window.location.search).get('audicode');
		const prString = new URLSearchParams(window.location.search).get('pr');

		if (audiCode) {
			setEntryType('audicode');

			getCarByCode({ variables: { code: audiCode } }).then((res) => {
				if (res.data?.carByCode?.__typename === 'ConfiguredCar') {
					const carByCode = res.data?.carByCode;
					if (
						carByCode?.code?.rescueInfo?.hasRescueInfo &&
						carByCode?.code?.rescueInfo?.history?.find((z) =>
							Object.values(RelevantRescueCodes).some((y) => z.repairCode === y),
						)?.repairCode
					) {
						const adjustments = carByCode?.code?.rescueInfo?.history?.map(
							(h) => h.repairCode,
						) as RelevantRescueCodes[];
						setRescueInfoAdjustments({
							adjustments: [...new Set(adjustments)],
							urlAudicode: audiCode,
						});
						openDialog('rescueInfo');
					}
					setCarIdentifier(normalizeCarIdentifier(expect(carByCode?.id)));
					const formattedFinancingParameters = mapFinanceDataToRequest(
						carByCode?.prices?.financingParameters?.product?.id,
						carByCode?.prices?.financingParameters?.product?.productParameters,
					);

					setPresetFinanceProduct(formattedFinancingParameters);

					if (
						carByCode?.prices?.financingParameters?.product?.id &&
						carByCode?.prices?.financingParameters?.product?.productParameters
					) {
						const params = carByCode?.prices?.financingParameters?.product?.productParameters.map((a) => ({
							key: a?.key,
							value: a?.value,
						}));
						if (params) {
							setInitFinancingInput({
								product: carByCode?.prices?.financingParameters?.product?.id,
								productParameters: params as InputMaybe<FinancingProductParameterInput>[],
							});
						}
					}
					const newUrl = `${window.location.pathname}`;
					window.history.replaceState({ path: newUrl }, '', newUrl);
				}
			});
		} else if (prString) {
			setEntryType('pr string');

			const configuredCarIdentifier: ConfiguredCarIdentifier = prStringToConfiguredCarInput(
				decodeURI(prString),
				aemConfig?.market,
				aemConfig?.language,
				'A',
				true,
			) as ConfiguredCarIdentifier;
			setCarIdentifier(
				normalizeCarIdentifier(
					expect(configuredCarIdentifier, 'expected configuredCarIdentifier from prString'),
				),
			);
		} else {
			setEntryType('default');

			getDefaultCarIdentifier({
				variables: {
					carlineInput: {
						brand: 'A',
						carlineId: aemConfig?.carlineId,
						country: aemConfig?.market,
						language: aemConfig?.language,
					},
				},
			})
				.then((defaultCarIdentifierData) => {
					setCarIdentifier(
						normalizeCarIdentifier(
							expect(
								defaultCarIdentifierData?.data?.configuredCarByCarline.id,
								'expected default carIdentifier',
							),
						),
					);
				})
				.catch(() => {
					setCurrentView('error');
				});
		}
	}, [
		JSON.stringify(aemConfig),
		getCarByCode,
		getDefaultCarIdentifier,
		setCarIdentifier,
		setCurrentView,
		setEntryType,
	]);

	if (currentView === 'error') return <ErrorPage />;

	if (!carIdentifier) return null;

	return (
		<MarketConfigProvider
			aemConfig={aemConfig}
			initialCarIdentifier={carIdentifier}
			initialFinancialCalculatorSettings={initFinancingInput}>
			<ConfiguratorProvider>
				<MarketUiProvider aemConfig={aemConfig}>
					<AppStyle data-audi-core-tracking-exclude>
						<MarketRouter />
					</AppStyle>
				</MarketUiProvider>
			</ConfiguratorProvider>
		</MarketConfigProvider>
	);
}
